/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { isEqual, isEmpty } from 'lodash'
import moment from 'moment'

import {
  Table,
  MetricSourceName,
  Warning,
  Spinner,
  Modal,
  Graph
} from '../'

import {
  formatMetricValueByFieldType
} from '../../utils'

import {
  MetricsRows
} from './MetricsRows'

const displayMetricValue = (metricResult, family, group, metric, renderTemplate) => {
  let metricValue = null
  try {
    if (family === group && group === metric) {
      metricValue = metricResult[family]
    } else if (group === metric) {
      metricValue = metricResult[family][metric]
    } else {
      metricValue = metricResult[family][group][metric]
    }
  } catch (e) {
    // if anything goes wrong display '-' (e.g. metricResult !== null && !isEmpty(metricResult))
    metricValue = '-'
  }

  return typeof metricValue === 'object' && isEmpty(metricValue)
    ? '-'
    : metricValue
}

const formatMetricValue = (value, family, group, metric, renderTemplate) => {
  let metricValue = null

  try {
    if (family === group && group === metric) {
      metricValue = formatMetricValueByFieldType(value, renderTemplate[family])
    } else if (group === metric) {
      metricValue = formatMetricValueByFieldType(value, renderTemplate[family][metric])
    } else {
      metricValue = formatMetricValueByFieldType(value, renderTemplate[family][group][metric])
    }
  } catch (e) {
    // if anything goes wrong
    metricValue = null
  }

  return metricValue
}


export class MetricTable extends React.PureComponent {
  static propTypes = {
    objectId: PropTypes.string,
    metricSourceId: PropTypes.string.isRequired,
    latestMetrics: PropTypes.shape({
      result: PropTypes.object.isRequired,
      date: PropTypes.string.isRequired
    }),
    metricToCompare: PropTypes.string,
    metricResults: PropTypes.arrayOf(
      PropTypes.shape({
        result: PropTypes.arrayOf(PropTypes.object),
        date: PropTypes.string
      })
    ),
    dateRange: PropTypes.arrayOf(PropTypes.string).isRequired,
    renderTimestamp: PropTypes.number,
    availableMetricDateIndex: PropTypes.number.isRequired
  }

  state = {
    metricGraphInputs: null
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.renderTimestamp === nextProps.renderTimestamp &&
      isEqual(this.state.metricGraphInputs, nextState.metricGraphInputs)) {
      return false
    }

    return true
  }

  closeMetricGraphModal = () => this.setState({ metricGraphInputs: null })

  showMetricGraphModal = (renderTemplate) => {
    const { family, group, metric, results, dateRange } = this.state.metricGraphInputs
    let metricsByDate = []

    dateRange.forEach((date, index) => {
      let metricsOnDate = results.find(result => (
        moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
      ))

      if (metricsOnDate) {
        metricsByDate.push({
          x: index,
          y: displayMetricValue(metricsOnDate.result, family, group, metric, renderTemplate)
        })
      } else {
        metricsByDate.push({
          x: index,
          y: null
        })
      }
    })

    const yAxisLabels = [...new Set(metricsByDate.filter(metric => metric.y).map(metric => metric.y))]
    const container = document.getElementById("metricTableContainer")

    // https://stackoverflow.com/questions/1248081/get-the-browser-viewport-dimensions-with-javascript
    const viewportWidth = Math.max(container.clientWidth, window.innerWidth || 0)
    const windowInnerHeight = window.innerHeight

    return (
      <Modal
        heading={`${family} ${group === null ? `| ${metric}` : `| ${group} | ${metric}`}`}
        body={
          <Graph
            width={viewportWidth - viewportWidth * 0.15}
            height={windowInnerHeight - windowInnerHeight * 0.25}
            horizontalGridLines={true}
            verticalGridLines={true}
            lineMarkSeries={[{ data: metricsByDate }]}
            xAxis={{
              title: 'Date range',
              tickTotal: metricsByDate.length,
              tickFormat: (tick, index) => dateRange[tick],
              tickLabelAngle: -60
            }}
            yAxis={{
              tickValues: yAxisLabels.length > 0 &&
                (isNaN(parseFloat(yAxisLabels[0])) ||  // value must not be decimal
                  isNaN(yAxisLabels[0])  // value must not be number
                )
                ? yAxisLabels
                : null,
              // tickTotal: yAxisLabels.length,
              tickFormat: (tick, index) => formatMetricValue(tick, family, group, metric, renderTemplate)
            }}
            margin={{ left: 80, right: 30, top: 10, bottom: 80 }}  // Default: {left: 40, right: 10, top: 10, bottom: 40}
            hintFormat={value => {
              const renderValue = formatMetricValue(value.y, family, group, metric, renderTemplate)

              return (
                <div className='metric-chart-hint'>
                  {renderValue}
                </div>
              )
            }}
            allowZoom={true}
          />
        }
        sizeXLarge={true}
        onCloseClick={this.closeMetricGraphModal}
      />
    )
  }

  render() {
    let { objectId, metricSourceId, latestMetrics, metricToCompare, metricGroupToCompare, metricFamilyToCompare, metricResults,
      dateRange, availableMetricDateIndex, renderTimestamp, renderTemplate } = this.props

    if (renderTimestamp === null) {
      return <Spinner type='tail' />
    }

    if (metricGroupToCompare && metricToCompare) {
      return (
        <div id='metricTableContainer' className='slds-scrollable_x slds-p-bottom_x-large'>
          <MetricSourceName
            id={metricSourceId}
            className='u-font-size-large slds-m-bottom_small'
          />
          <Table
            columns={['Metric family', 'Metric group', 'Metric', ...dateRange]}
            rows={[
              <MetricsRows
                objectId={objectId}
                metricSourceId={metricSourceId}
                latestMetrics={latestMetrics}
                metricFamilyToCompare={metricFamilyToCompare === 'all' ? null : metricFamilyToCompare}
                metricGroupToCompare={metricGroupToCompare === 'all' ? null : metricGroupToCompare}
                metricToCompare={metricToCompare === 'all' ? null : metricToCompare}
                metricResults={metricResults}
                dateRange={dateRange}
                availableMetricDateIndex={availableMetricDateIndex}
                key={`metricRows_${metricFamilyToCompare}_${metricGroupToCompare}_${metricToCompare}`}
                renderTemplate={renderTemplate}
                onRowClick={(family, group, metric, results, dateRange) => (
                  this.setState({ metricGraphInputs: { family, group, metric, results, dateRange } })
                )}
              />
            ]}
            objectRows={true}
            className='metric-comparison-table'
            key={`metricRows_${metricFamilyToCompare}_${metricGroupToCompare}_${metricToCompare}`}
          />
          {this.state.metricGraphInputs !== null && this.showMetricGraphModal(renderTemplate)}
        </div>
      )
    } else {
      return (
        <Warning>
          Rendering of results not possible.
        </Warning>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/metric-comparison-table/MetricTable.js